import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { maternityService } from '../services/maternityService'
import { Pregnancy, MaternityVisit } from '../types/maternity'
import { formatDate } from '../utils/locale'
import { calculateGestationalAge } from '../utils/dueDateCalculator'
import { Modal } from './Modal'
import { MaternityVisitForm } from './MaternityVisitForm'
import { MaternityOutcomeForm } from './MaternityOutcomeForm'
import { DueDateDisplay } from './DueDateDisplay'
import './PregnancyDetail.css'

interface PregnancyDetailProps {
  pregnancy: Pregnancy
  patientId: string
  onBack?: () => void
  onPregnancyUpdated?: (pregnancy: Pregnancy) => void
  readOnly?: boolean
}

export function PregnancyDetail({ pregnancy, patientId, onBack, onPregnancyUpdated, readOnly = false }: PregnancyDetailProps) {
  const queryClient = useQueryClient()
  const [showVisitForm, setShowVisitForm] = useState(false)
  const [editingVisit, setEditingVisit] = useState<MaternityVisit | undefined>()
  const [showOutcomeForm, setShowOutcomeForm] = useState(false)
  const [isGeneratingClaimForm, setIsGeneratingClaimForm] = useState(false)
  const canWrite = !readOnly

  const { data: visits = [], isLoading: visitsLoading } = useQuery({
    queryKey: ['maternity-visits', pregnancy.pregnancyId],
    queryFn: () => maternityService.getVisits(pregnancy.pregnancyId),
    enabled: !!pregnancy.pregnancyId,
  })

  const gestationalAge = calculateGestationalAge(pregnancy.lastMenstrualPeriod)
  const isActive = pregnancy.status === 'active'

  const handleAddVisit = () => {
    if (!canWrite) return
    setEditingVisit(undefined)
    setShowVisitForm(true)
  }

  const handleEditVisit = (visit: MaternityVisit) => {
    if (!canWrite) return
    setEditingVisit(visit)
    setShowVisitForm(true)
  }

  const handleRecordOutcome = () => {
    if (!canWrite) return
    setShowOutcomeForm(true)
  }

  const handleFormSuccess = () => {
    setShowVisitForm(false)
    setEditingVisit(undefined)
    queryClient.invalidateQueries({ queryKey: ['maternity-visits', pregnancy.pregnancyId] })
  }

  const handleOutcomeSuccess = (updatedPregnancy?: Pregnancy) => {
    setShowOutcomeForm(false)
    queryClient.invalidateQueries({ queryKey: ['pregnancies', patientId] })
    queryClient.invalidateQueries({ queryKey: ['pregnancy', pregnancy.pregnancyId] })
    if (updatedPregnancy) {
      onPregnancyUpdated?.(updatedPregnancy)
    }
  }

  const handleGenerateClaimForm = async () => {
    try {
      setIsGeneratingClaimForm(true)
      const result = await maternityService.generateClaimForm(pregnancy.pregnancyId, patientId)
      
      // Download the file
      const blob = new Blob([atob(result.content)], { type: result.mimeType })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = result.fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error generating claim form:', error)
      alert('Failed to generate claim form. Please try again.')
    } finally {
      setIsGeneratingClaimForm(false)
    }
  }

  return (
    <div className="pregnancy-detail">
      {onBack && (
        <button className="btn-link back-button" onClick={onBack}>
          ← Back to Pregnancies
        </button>
      )}

      <div className="pregnancy-detail-header">
        <div>
          <h2>Pregnancy #{pregnancy.pregnancyNumber}</h2>
          <span className={`status-badge ${pregnancy.status}`}>{pregnancy.status}</span>
        </div>
        <div className="pregnancy-actions">
          {isActive && (
            <>
              <button
                className="btn-primary"
                onClick={handleAddVisit}
                disabled={!canWrite}
                title={canWrite ? undefined : 'Secondary records are read-only'}
              >
                Record Visit
              </button>
              <button
                className="btn-secondary"
                onClick={handleRecordOutcome}
                disabled={!canWrite}
                title={canWrite ? undefined : 'Secondary records are read-only'}
              >
                Record Outcome
              </button>
            </>
          )}
          <button
            className="btn-secondary"
            onClick={handleGenerateClaimForm}
            disabled={isGeneratingClaimForm}
          >
            {isGeneratingClaimForm ? 'Generating...' : 'Generate Claim Form'}
          </button>
        </div>
      </div>

      <div className="pregnancy-info-grid">
        <div className="info-card">
          <h3>Dates</h3>
          <div className="info-item">
            <span className="label">Last Menstrual Period:</span>
            <span className="value">{formatDate(pregnancy.lastMenstrualPeriod)}</span>
          </div>
          <div className="info-item">
            <span className="label">Estimated Due Date:</span>
            <DueDateDisplay
              lmpDate={pregnancy.lastMenstrualPeriod}
              eddDate={pregnancy.estimatedDueDate}
            />
          </div>
          {isActive && (
            <div className="info-item">
              <span className="label">Current Gestational Age:</span>
              <span className="value">{gestationalAge.display}</span>
            </div>
          )}
        </div>

        {pregnancy.outcome && (
          <div className="info-card">
            <h3>Outcome</h3>
            <div className="info-item">
              <span className="label">Outcome:</span>
              <span className="value">{pregnancy.outcome}</span>
            </div>
            {pregnancy.outcomeDate && (
              <div className="info-item">
                <span className="label">Outcome Date:</span>
                <span className="value">{formatDate(pregnancy.outcomeDate)}</span>
              </div>
            )}
            {pregnancy.gestationalAgeAtOutcome && (
              <div className="info-item">
                <span className="label">Gestational Age at Outcome:</span>
                <span className="value">{pregnancy.gestationalAgeAtOutcome}</span>
              </div>
            )}
            {pregnancy.deliveryMethod && (
              <div className="info-item">
                <span className="label">Delivery Method:</span>
                <span className="value">{pregnancy.deliveryMethod}</span>
              </div>
            )}
            {pregnancy.deliveryLocation && (
              <div className="info-item">
                <span className="label">Delivery Location:</span>
                <span className="value">{pregnancy.deliveryLocation}</span>
              </div>
            )}
          </div>
        )}

        {pregnancy.babies && pregnancy.babies.length > 0 && (
          <div className="info-card">
            <h3>Babies ({pregnancy.babies.length})</h3>
            {pregnancy.babies.map((baby, index) => (
              <div key={index} className="baby-info">
                {baby.name && (
                  <div className="info-item">
                    <span className="label">Name:</span>
                    <span className="value">{baby.name}</span>
                  </div>
                )}
                {baby.gender && (
                  <div className="info-item">
                    <span className="label">Gender:</span>
                    <span className="value">{baby.gender}</span>
                  </div>
                )}
                {baby.birthWeight && (
                  <div className="info-item">
                    <span className="label">Birth Weight:</span>
                    <span className="value">{baby.birthWeight} kg</span>
                  </div>
                )}
                {baby.birthLength && (
                  <div className="info-item">
                    <span className="label">Birth Length:</span>
                    <span className="value">{baby.birthLength} cm</span>
                  </div>
                )}
                {baby.babyId && (
                  <div className="info-item">
                    <span className="label">Patient ID:</span>
                    <span className="value">
                      <a href={`/patients/${baby.babyId}`} className="link">
                        View Patient →
                      </a>
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {pregnancy.notes && (
          <div className="info-card">
            <h3>Notes</h3>
            <p>{pregnancy.notes}</p>
          </div>
        )}
      </div>

      <div className="visits-section">
        <div className="visits-header">
          <h3>Visits ({visits.length})</h3>
          {isActive && (
            <button className="btn-primary" onClick={handleAddVisit}>
              Add Visit
            </button>
          )}
        </div>

        {visitsLoading ? (
          <div className="loading">Loading visits...</div>
        ) : visits.length === 0 ? (
          <div className="empty-state">
            <p>No visits recorded for this pregnancy.</p>
            {isActive && (
              <button className="btn-primary" onClick={handleAddVisit}>
                Record First Visit
              </button>
            )}
          </div>
        ) : (
          <div className="visits-list">
            {visits.map((visit) => (
              <div key={visit.visitId} className="visit-card">
                <div className="visit-card-header">
                  <div>
                    <strong>{formatDate(visit.visitDate)}</strong>
                    <span className="visit-type-badge">{visit.visitType}</span>
                    <span className="gestational-age">{visit.gestationalAge}</span>
                  </div>
                  {isActive && (
                    <button
                      className="btn-link"
                      onClick={() => handleEditVisit(visit)}
                      disabled={!canWrite}
                      title={canWrite ? undefined : 'Secondary records are read-only'}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <div className="visit-card-body">
                  {visit.bloodPressure && (
                    <div className="visit-field">
                      <span className="field-label">BP:</span>
                      <span className="field-value">
                        {visit.bloodPressure.systolic}/{visit.bloodPressure.diastolic}
                      </span>
                    </div>
                  )}
                  {visit.weight && (
                    <div className="visit-field">
                      <span className="field-label">Weight:</span>
                      <span className="field-value">{visit.weight} kg</span>
                    </div>
                  )}
                  {visit.fundalHeight && (
                    <div className="visit-field">
                      <span className="field-label">Fundal Height:</span>
                      <span className="field-value">{visit.fundalHeight} cm</span>
                    </div>
                  )}
                  {visit.fetalHeartRate && (
                    <div className="visit-field">
                      <span className="field-label">Fetal HR:</span>
                      <span className="field-value">{visit.fetalHeartRate} bpm</span>
                    </div>
                  )}
                  {visit.interventions && visit.interventions.length > 0 && (
                    <div className="visit-interventions">
                      <strong>Interventions ({visit.interventions.length}):</strong>
                      <ul>
                        {visit.interventions.map((intervention) => (
                          <li key={intervention.id}>
                            {intervention.type}: {intervention.description}
                            {intervention.performedAt && (
                              <span className="intervention-meta">
                                {' '}
                                (by {intervention.performedBy} on{' '}
                                {formatDate(intervention.performedAt)})
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {visit.notes && (
                    <div className="visit-notes">
                      <strong>Notes:</strong>
                      <p>{visit.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={canWrite && showVisitForm}
        title={editingVisit ? 'Edit Visit' : 'Record Visit'}
        onClose={() => {
          setShowVisitForm(false)
          setEditingVisit(undefined)
        }}
        size="large"
      >
        <MaternityVisitForm
          patientId={patientId}
          pregnancyId={pregnancy.pregnancyId}
          visit={editingVisit}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowVisitForm(false)
            setEditingVisit(undefined)
          }}
        />
      </Modal>

      <Modal
        isOpen={canWrite && showOutcomeForm}
        title="Record Pregnancy Outcome"
        onClose={() => setShowOutcomeForm(false)}
        size="large"
      >
        <MaternityOutcomeForm
          pregnancy={pregnancy}
          patientId={patientId}
          onSuccess={(updated) => handleOutcomeSuccess(updated)}
          onCancel={() => setShowOutcomeForm(false)}
        />
      </Modal>
    </div>
  )
}
